import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Send, User, Clock, FileText } from "lucide-react";
import { CannedResponseManager } from "./CannedResponseManager";

interface TicketDetailViewProps {
  ticket: any;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  admins: any[];
}

export function TicketDetailView({
  ticket,
  open,
  onClose,
  onUpdate,
  admins,
}: TicketDetailViewProps) {
  const { toast } = useToast();
  const [replyContent, setReplyContent] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [sending, setSending] = useState(false);

  async function handleSendReply() {
    if (!replyContent.trim()) return;

    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Log reply (types will be generated)
      toast({
        title: "Reply Sent",
        description: "User will be notified via email",
      });

      // Update ticket status and first response time
      await supabase
        .from('support_tickets')
        .update({
          status: 'in_progress',
          first_response_at: ticket.first_response_at || new Date().toISOString(),
        })
        .eq('id', ticket.id);

      setReplyContent("");
      onUpdate();
    } catch (error: any) {
      console.error("Reply error:", error);
      toast({
        title: "Error",
        description: "Failed to send reply",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  }

  async function handleSendInternalNote() {
    if (!internalNote.trim()) return;

    try {
      toast({
        title: "Internal Note Added",
        description: "Note saved for admin team",
      });
      setInternalNote("");
    } catch (error: any) {
      console.error("Note error:", error);
      toast({
        title: "Error",
        description: "Failed to save note",
        variant: "destructive",
      });
    }
  }

  async function handleStatusChange(newStatus: string) {
    try {
      const { error } = await supabase
        .from("support_tickets")
        .update({ status: newStatus })
        .eq("id", ticket.id);

      if (error) throw error;

      toast({ title: "Status Updated" });
      onUpdate();
    } catch (error: any) {
      console.error("Status update error:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  }

  async function handlePriorityChange(newPriority: string) {
    try {
      const { error } = await supabase
        .from("support_tickets")
        .update({ priority: newPriority })
        .eq("id", ticket.id);

      if (error) throw error;

      toast({ title: "Priority Updated" });
      onUpdate();
    } catch (error: any) {
      console.error("Priority update error:", error);
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive",
      });
    }
  }

  async function handleAssignChange(adminId: string) {
    try {
      const { error } = await supabase
        .from("support_tickets")
        .update({ assigned_to: adminId })
        .eq("id", ticket.id);

      if (error) throw error;

      toast({ title: "Ticket Assigned" });
      onUpdate();
    } catch (error: any) {
      console.error("Assignment error:", error);
      toast({
        title: "Error",
        description: "Failed to assign ticket",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ticket #{ticket.id.substring(0, 8)}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Ticket Info */}
            <div>
              <h3 className="font-semibold text-lg">{ticket.subject}</h3>
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{ticket.profiles?.full_name || "Unknown User"}</span>
                <Separator orientation="vertical" className="h-4" />
                <Clock className="h-4 w-4" />
                <span>{format(new Date(ticket.created_at), "PPp")}</span>
              </div>
            </div>

            <Separator />

            {/* Original Message */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="text-sm whitespace-pre-wrap">{ticket.message}</div>
            </div>

            <Separator />

            {/* Reply Section with Tabs */}
            <Tabs defaultValue="reply" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="reply">Reply to User</TabsTrigger>
                <TabsTrigger value="internal">Internal Note</TabsTrigger>
                <TabsTrigger value="canned">Canned Responses</TabsTrigger>
              </TabsList>

              <TabsContent value="reply" className="space-y-3">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Type your response to the user..."
                  rows={8}
                />
                <Button onClick={handleSendReply} disabled={sending || !replyContent.trim()}>
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? "Sending..." : "Send Reply"}
                </Button>
              </TabsContent>

              <TabsContent value="internal" className="space-y-3">
                <Textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Add an internal note for the admin team (not visible to user)..."
                  rows={8}
                  className="bg-yellow-50 dark:bg-yellow-950/20"
                />
                <Button onClick={handleSendInternalNote} disabled={!internalNote.trim()}>
                  <FileText className="h-4 w-4 mr-2" />
                  Save Internal Note
                </Button>
              </TabsContent>

              <TabsContent value="canned">
                <CannedResponseManager
                  onSelectResponse={(content) => {
                    setReplyContent(content);
                    // Switch to reply tab
                    const replyTab = document.querySelector('[value="reply"]') as HTMLElement;
                    replyTab?.click();
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Context Panel */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select value={ticket.status} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_user">Waiting User</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Priority</label>
              <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Assign To</label>
              <Select
                value={ticket.assigned_to || "unassigned"}
                onValueChange={handleAssignChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {admins.map((admin) => (
                    <SelectItem key={admin.user_id} value={admin.user_id}>
                      {admin.profiles?.full_name || "Unknown"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-medium mb-2">User Info</h4>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Name:</span>{" "}
                  {ticket.profiles?.full_name || "N/A"}
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>{" "}
                  {ticket.profiles?.email || "N/A"}
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">Ticket Info</h4>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Category:</span>{" "}
                  <Badge variant="outline">{ticket.category}</Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span>{" "}
                  {format(new Date(ticket.created_at), "PP")}
                </div>
                <div>
                  <span className="text-muted-foreground">Last Activity:</span>{" "}
                  {format(new Date(ticket.last_activity_at || ticket.created_at), "PP")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
